import { normalizeInstanceUrl } from './config.ts';

/**
 * §3 — deploy-level default instance. First match wins:
 *
 * 1. `?instance=` URL parameter — one-shot, for sharing a link.
 * 2. the user's saved setting (per-origin `localStorage`, supplied by the platform).
 * 3. `/config.json`, fetched at boot — the deploy-level knob, edited next to `index.html`.
 * 4. build-time `VITE_DEFAULT_INSTANCE`.
 *
 * A candidate that fails to normalize is *skipped*, not fatal — §3 is explicit that a missing or
 * malformed `config.json` falls through to step 4 rather than breaking boot.
 */
export type InstanceSource = 'url-param' | 'saved-setting' | 'config-json' | 'build-default';

export interface InstanceCandidates {
  readonly urlParam?: string | null;
  readonly savedSetting?: string | null;
  /** The parsed body of `/config.json`, whatever it turned out to be. `JSON.parse` stays outside core (R4). */
  readonly configJson?: unknown;
  readonly buildDefault?: string | null;
}

export interface ResolvedInstance {
  readonly instanceUrl: string;
  readonly source: InstanceSource;
}

/** `{ "instance": "https://invidious.example.com" }`, defensively read. */
export function readConfigJsonInstance(configJson: unknown): string | null {
  if (typeof configJson !== 'object' || configJson === null) return null;
  const value = (configJson as Record<string, unknown>)['instance'];
  return typeof value === 'string' ? value : null;
}

export function resolveDefaultInstance(candidates: InstanceCandidates): ResolvedInstance | null {
  const ordered: readonly { source: InstanceSource; value: string | null }[] = [
    { source: 'url-param', value: candidates.urlParam ?? null },
    { source: 'saved-setting', value: candidates.savedSetting ?? null },
    { source: 'config-json', value: readConfigJsonInstance(candidates.configJson) },
    { source: 'build-default', value: candidates.buildDefault ?? null },
  ];

  for (const { source, value } of ordered) {
    if (value === null) continue;
    const normalized = normalizeInstanceUrl(value);
    if (normalized.ok) return { instanceUrl: normalized.value, source };
  }
  return null;
}
